import * as Sentry from "@sentry/browser";
import { getConfigSync } from "storefront/GrailedAPI/v1/Config/getConfig";
import { isBrowserContext } from "storefront/lib/isBrowserContext";
import GrailedAPIError from "storefront/GrailedAPI/Error";
import { fingerprintGrailedAPIError } from "storefront/lib/fingerprintGrailedAPIError";

// Stores the initialized Sentry
let instance: typeof Sentry | null = null;

/**
 * @name Sentry.getInstance
 * @description Returns and initialized Sentry (error tracking service)
 */
export const getInstance = (): typeof Sentry => {
  // Skip initialization during SSR — @sentry/browser has no transport on the
  // server so init would be a no-op anyway, and PublicConfig is not available
  // synchronously until _document.tsx injects window.PUBLIC_CONFIG.
  if (instance === null && isBrowserContext()) {
    const config = getConfigSync();

    Sentry.init({
      dsn: config.sentry.storefrontDsn,
      environment: config.env || "development",
      ignoreErrors: [
        /TypeError: t is not a function. \(In 't\(\)', 't' is undefined\)$/,
        // Noise from third-party/extension code calling global logMutedMessage in page context
        /^ReferenceError: Can't find variable: logMutedMessage$/,
      ],
      // Errors inside OneTrust's consent banner SDK are not in our control
      denyUrls: [/cdn\.cookielaw\.org/, /otBannerSdk\.js/],
      beforeSend: (event, hint) => {
        const exception = hint?.originalException;
        if (exception instanceof GrailedAPIError) {
          const fingerprint = fingerprintGrailedAPIError(exception);
          if (fingerprint) return { ...event, fingerprint };
        }
        return event;
      },
    });
    instance = Sentry;
  }

  return Sentry;
};
